import { useMemo, useState } from 'react';
import { convertHexToContentColorKeys, Typography } from '@banhala/ui-kit';
import { useForegroundInterval } from '../../../../../hooks';
import { isNil } from '../../../../../utils';
import type { PromotionShortBannerTimer } from '../types';

type RemainTimerProps = {
  timer: PromotionShortBannerTimer;
};

export const RemainTimer = ({ timer }: RemainTimerProps) => {
  const { isTimeOver, text } = useRemainTimeText(timer.expired_at);

  if (isTimeOver) return null;

  return (
    <Typography
      type={'subtitle4'}
      color={convertHexToContentColorKeys(timer.text_color) ?? undefined}
      rawColor={
        convertHexToContentColorKeys(timer.text_color)
          ? undefined
          : timer.text_color
      }
    >
      {text} 남음
    </Typography>
  );
};

const useRemainTimeText = (expiredAt: string | null) => {
  const [now, setNow] = useState(Date.now());

  useForegroundInterval(
    () => {
      setNow(Date.now());
    },
    isNil(expiredAt) ? null : 1000,
  );

  return useMemo(() => {
    if (isNil(expiredAt)) return { isTimeOver: false, text: '' };
    const diff = new Date(expiredAt).getTime() - now;
    if (diff <= 0) return { isTimeOver: true, text: '' };

    const totalHours = Math.floor(diff / (1000 * 60 * 60));
    if (totalHours >= 24) {
      return { isTimeOver: false, text: `${Math.floor(totalHours / 24)}일` };
    }
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    const hh = String(totalHours).padStart(2, '0');
    const mm = String(minutes).padStart(2, '0');
    const ss = String(seconds).padStart(2, '0');
    return { isTimeOver: false, text: `${hh} : ${mm} : ${ss}` };
  }, [expiredAt, now]);
};
